import { useCallback, useState } from "react"
import { getSession, getAccessToken } from "src/redux/slicers/userData"

const CHAT_API_URL =
  window.location.hostname === "localhost"
    ? "https://bedrock-agentcore.us-east-1.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-east-1%3A004819182232%3Aruntime%2Fchat_server_v2_yceuhyn4mreaxg5b7bcr2yjqqy-CvNiaVCp2f/invocations?qualifier=DEFAULT"
    : "https://bedrock-agentcore.us-east-1.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-east-1%3A004819182232%3Aruntime%2Fchat_server_v2_yceuhyn4mreaxg5b7bcr2yjqqy-CvNiaVCp2f/invocations?qualifier=DEFAULT"

export function useGetSuggestions() {
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)

  const fetchSuggestions = useCallback(async () => {
    setIsLoadingSuggestions(true)
    try {
      const idToken = await getSession(true)
      const accessToken = await getAccessToken()

      const response = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json, text/event-stream"
        },
        body: JSON.stringify({
          action: "get_suggestions",
          id_token: idToken,
          limit: 5
        })
      })

      if (!response.ok) {
        setSuggestions([])
        return
      }

      // Stream the body chunk by chunk (same approach as the reference HTML)
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let buffer = ""

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
          }
        } finally {
          reader.releaseLock()
        }
      }

      // Parse: collect all `data: ` line values (handles multi-event SSE and CRLF)
      let jsonString = ""
      for (const line of buffer.split("\n")) {
        const trimmed = line.trim() // trims \r from CRLF too
        if (trimmed.startsWith("data: ")) {
          jsonString += trimmed.slice(6)
        } else if (trimmed.startsWith("{")) {
          jsonString += trimmed
        }
      }

      // Fallback: treat whole buffer as plain JSON
      if (!jsonString) jsonString = buffer.trim()

      let parsed: unknown
      try {
        parsed = JSON.parse(jsonString)
      } catch {
        // Last resort: regex-extract a JSON object containing "suggestions"
        const match = buffer.match(/\{[\s\S]*?"suggestions"[\s\S]*?\}/)
        if (match) {
          try {
            parsed = JSON.parse(match[0])
          } catch {
            setSuggestions([])
            return
          }
        } else {
          setSuggestions([])
          return
        }
      }

      const list: string[] = Array.isArray(parsed)
        ? (parsed as string[])
        : Array.isArray((parsed as Record<string, unknown>)?.suggestions)
        ? ((parsed as Record<string, unknown>).suggestions as string[])
        : []
      setSuggestions([...new Set(list)].reverse())
    } catch {
      setSuggestions([])
    } finally {
      setIsLoadingSuggestions(false)
    }
  }, [])

  const clearSuggestions = useCallback(() => setSuggestions([]), [])

  return {
    suggestions,
    isLoadingSuggestions,
    fetchSuggestions,
    clearSuggestions
  }
}

